/**
 * A tiny router. Production uses real paths (the Worker serves index.html for every route); the demo build uses
 * hash routes so it works from any static host, including an Artifact.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent } from 'react';

const HASH = import.meta.env.MODE === 'demo';

export interface Location {
  path: string;
  query: URLSearchParams;
  hash: string;
}

function read(): Location {
  if (HASH) {
    const raw = window.location.hash.replace(/^#/, '') || '/';
    const [pathAndQuery, hash = ''] = raw.split('#');
    const [path, q = ''] = pathAndQuery!.split('?');
    return { path: path || '/', query: new URLSearchParams(q), hash };
  }
  return { path: window.location.pathname, query: new URLSearchParams(window.location.search), hash: window.location.hash.replace(/^#/, '') };
}

export function href(to: string): string {
  return HASH ? `#${to}` : to;
}

type Guard = (to: string) => boolean;

const RouterContext = createContext<{ loc: Location; navigate: (to: string, opts?: { replace?: boolean; force?: boolean }) => void; setGuard: (g: Guard | null) => void }>(null!);

export function RouterProvider({ children }: { children: React.ReactNode }) {
  const [loc, setLoc] = useState(read);
  const [guard, setGuardState] = useState<{ g: Guard | null }>({ g: null });

  useEffect(() => {
    const onChange = () => setLoc(read());
    window.addEventListener(HASH ? 'hashchange' : 'popstate', onChange);
    return () => window.removeEventListener(HASH ? 'hashchange' : 'popstate', onChange);
  }, []);

  const navigate = useCallback(
    (to: string, opts: { replace?: boolean; force?: boolean } = {}) => {
      if (!opts.force && guard.g && !guard.g(to)) return;
      if (HASH) {
        if (opts.replace) window.location.replace(`#${to}`);
        else window.location.hash = to;
      } else {
        if (opts.replace) window.history.replaceState(null, '', to);
        else window.history.pushState(null, '', to);
        setLoc(read());
      }
      window.scrollTo(0, 0);
    },
    [guard],
  );

  const setGuard = useCallback((g: Guard | null) => setGuardState({ g }), []);
  const value = useMemo(() => ({ loc, navigate, setGuard }), [loc, navigate, setGuard]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export const useRouter = () => useContext(RouterContext);

export function Link({ to, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const { navigate } = useRouter();
  return (
    <a
      {...rest}
      href={href(to)}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
    />
  );
}

/** Match `/s/:site/c/:collection` style patterns. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('/').filter(Boolean);
  if (p.length !== a.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i]!.startsWith(':')) out[p[i]!.slice(1)] = decodeURIComponent(a[i]!);
    else if (p[i] !== a[i]) return null;
  }
  return out;
}
